import type { GraphNode, GraphEdge } from '@cade/shared-types';
import type { GraphElementDefinition } from './types';

/**
 * Convert a graph_data WebSocket event's nodes/edges into
 * platform-agnostic GraphElementDefinition objects.
 *
 * Node properties are spread into the data object so Cytoscape
 * or any other renderer can access them directly.
 */
export function graphEventToElements(event: {
  nodes: GraphNode[];
  edges: GraphEdge[];
}): GraphElementDefinition[] {
  const elements: GraphElementDefinition[] = [];

  // Node types that are "properties" of another entity/activity rather than
  // first-class entities (location of an entity, MITRE technique of an activity).
  // Edges touching these render as muted dotted "property" edges (see CytoscapeRenderer).
  const PROPERTY_NODE_TYPES = new Set(['geolocation', 'mitre_technique']);
  const nodeTypeById = new Map<string, string>();
  for (const node of event.nodes) {
    nodeTypeById.set(node.id, node.type);
  }

  for (const node of event.nodes) {
    elements.push({
      group: 'nodes',
      data: {
        id: node.id,
        label: node.label,
        type: node.type,
        severity: node.severity,
        ...(node.properties ?? {}),
      },
    });
  }

  for (const edge of event.edges) {
    const isProperty =
      PROPERTY_NODE_TYPES.has(nodeTypeById.get(edge.source) ?? '') ||
      PROPERTY_NODE_TYPES.has(nodeTypeById.get(edge.target) ?? '');
    elements.push({
      group: 'edges',
      data: {
        id: `${edge.source}-${edge.type}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        label: edge.label ?? edge.type.replace(/_/g, ' '),
        style: edge.style,
        // Additive boolean flag only — color/style resolved in the renderer.
        isProperty,
      },
    });
  }

  return elements;
}
